import type * as React from "react";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { CircleDashed } from "@phosphor-icons/react/CircleDashed";
import { HandTap } from "@phosphor-icons/react/HandTap";
import { Polygon } from "@phosphor-icons/react/Polygon";
import { SelectionAll } from "@phosphor-icons/react/SelectionAll";
import { SelectionPlus } from "@phosphor-icons/react/SelectionPlus";

import type { BaseComponentProps, QuerySchema, SpatialTool } from "../types";

export interface SpatialSelectionProps extends BaseComponentProps {
	/** Query schema containing spatial selection configuration */
	schema: QuerySchema;
	/** Currently selected spatial tool */
	value: string | null;
	/** Callback when spatial tool selection changes */
	onChange: (toolId: string | null) => void;
}

// Available spatial selection tools
const SPATIAL_TOOLS: SpatialTool[] = [
	{
		id: "point",
		name: "Point Selection",
		description: "Click on the map to select individual features",
		icon: HandTap,
		requiresMapInteraction: true,
	},
	{
		id: "rectangle",
		name: "Rectangle Selection",
		description: "Draw a rectangle to select features within the area",
		icon: SelectionPlus,
		requiresMapInteraction: true,
	},
	{
		id: "circle",
		name: "Circle Selection",
		description: "Draw a circle to select features within the area",
		icon: CircleDashed,
		requiresMapInteraction: true,
	},
	{
		id: "polygon",
		name: "Polygon Selection",
		description: "Draw a custom polygon to select features within the area",
		icon: Polygon,
		requiresMapInteraction: true,
	},
	{
		id: "all",
		name: "Select All",
		description: "Select all features in the layer without map interaction",
		icon: SelectionAll,
		requiresMapInteraction: false,
	},
];

export function SpatialSelection({
	schema,
	value,
	onChange,
	disabled = false,
}: SpatialSelectionProps): React.JSX.Element | null {
	if (!schema.spatialSelection?.enabled) {
		return null;
	}

	const availableTools = SPATIAL_TOOLS.filter((tool) =>
		schema.spatialSelection?.tools.includes(tool.id),
	);

	const selectedTool = availableTools.find((tool) => tool.id === value);

	const handleToggleChange = (
		_: React.MouseEvent<HTMLElement>,
		newValue: string | null,
	) => {
		onChange(newValue);
	};

	const handleKeyUp = (event: React.KeyboardEvent) => {
		if (event.key === "Enter" || event.key === " ") {
			// Cycle through available tools or clear selection
			const currentIndex = availableTools.findIndex(
				(tool) => tool.id === value,
			);
			const nextIndex = currentIndex + 1;
			const nextTool =
				nextIndex < availableTools.length ? availableTools[nextIndex] : null;
			onChange(nextTool?.id ?? null);
		}
	};

	return (
		<Stack spacing={2}>
			<Typography
				variant="overline"
				color="text.secondary">
				Selection
			</Typography>

			{/* Toggle Button Group */}
			<ToggleButtonGroup
				color="primary"
				exclusive
				value={value ?? null}
				onChange={handleToggleChange}
				onKeyUp={handleKeyUp}
				tabIndex={0}
				fullWidth
				disabled={disabled}
				sx={{ width: "100%" }}>
				{availableTools.map((tool) => {
					const IconComponent = tool.icon;

					return (
						<ToggleButton
							key={tool.id}
							value={tool.id}
							title={tool.description}
							sx={{
								flex: 1,
								minHeight: 48,
							}}>
							<IconComponent />
						</ToggleButton>
					);
				})}
			</ToggleButtonGroup>

			{/* Helper alert positioned below selection tools */}
			<Alert
				severity={
					selectedTool
						? selectedTool.requiresMapInteraction
							? "info"
							: "success"
						: "info"
				}>
				{selectedTool ? (
					<>
						<Typography
							variant="body2"
							sx={{ fontWeight: "medium" }}>
							{selectedTool.name} active
						</Typography>
						<Typography
							variant="body2"
							color="text.secondary">
							{selectedTool.requiresMapInteraction
								? "Click and interact with the map to define your selection area"
								: "All features in the layer will be selected automatically"}
						</Typography>
					</>
				) : (
					<Typography variant="body2">
						Choose a selection tool to filter features by location
					</Typography>
				)}
			</Alert>
		</Stack>
	);
}
